import React, { FC, Fragment, useMemo } from 'react';
import qs, { ParsedQuery } from 'query-string';
import { Platform as PlatformRN, StatusBar } from 'react-native';
import { omit, sortBy } from 'lodash';
import { useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import BindingComponent from '@common/screens/RenderScreen/BindingComponent';
import { AppVersion, Platform } from 'click-types';
import { ElementType, ObjectRenderProps } from '@common/types/element';
import { appInfoSelector } from '@common/redux/selectors/app';
import { HEIGHT_STATUS_BAR } from '@common/constants/shared';
import { dimensionSelector, pagesSelector } from '@common/redux/selectors/page';
import { mappingV4 } from './func';
import { mapV3StyleToV4Style, updateElementColor } from '@common/utils/color';
import { useLocation } from '@common/routes/hooks';

export const getNumber = (input: string) => {
  const regex = /\d+/gm;
  let m;

  while ((m = regex.exec(input)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (m.index === regex.lastIndex) {
      regex.lastIndex++;
    }

    return parseInt(m[0]);
  }
};

export const isPercentDimension = (input: string | number) => {
  if (typeof input === 'number') {
    return false;
  }

  const regex = /[%]+/g;
  return regex.test(input);
};

/**
 * calculate position style of object, included (top, left, bottom, right)
 * @param obj
 * @param parent
 * @returns
 *
 * Example:
 * obj.style = {
 *  width: '100px',
 *  height: '100px',
 *  top: '10px',
 *  left: '50%',
 * }
 * parent = {
 *  width: 375,
 *  height: 812,
 * }
 * return {
 *  top: 10,
 *  left: 137.5, // = 50% * (375 - 100)
 * }
 */
const calculateObjectPositionStyle = (
  obj: ElementType,
  parent: {
    width: number;
    height: number;
  }
) => {
  const widthValue =
    obj?.style?.width && typeof obj.style?.width !== 'string'
      ? obj.style?.width
      : obj?.width;
  const heightValue =
    obj?.style?.height && typeof obj.style?.height !== 'string'
      ? obj.style?.height
      : obj?.height;
  const width = isPercentDimension(widthValue)
    ? (parent.width * Number(widthValue)) / 100
    : Number(widthValue);
  const height = isPercentDimension(heightValue)
    ? (parent.height * Number(heightValue)) / 100
    : Number(heightValue);

  const getTopValue = () => {
    const value = obj.style?.top ?? obj.y;
    if (isPercentDimension(value)) {
      if (value === '50%' && PlatformRN.OS === 'web') {
        // for web use transform translate to keep element center-center
        return value;
      }
      const numberValue = getNumber(value);
      return (Number(numberValue) / 100) * (parent.height - height);
    }
    return value;
  };

  const getLeftValue = () => {
    const value = obj.style?.left ?? obj.x;
    if (isPercentDimension(value)) {
      if (value === '50%' && PlatformRN.OS === 'web') {
        // for web use transform translate to keep element center-center
        return value;
      }

      if (PlatformRN.OS !== 'web' && value === '50%') {
        let width = obj.style?.width || 0;
        if (isPercentDimension(width)) {
          width = getNumber(width);
          width = parent.width * (width / 100);
        }
        return (parent.width - width) / 2;
      }
      const numberValue = getNumber(value);
      return (Number(numberValue) / 100) * (parent.width - width);
    }
    return value;
  };

  const getBottomValue = () => {
    const value = obj.style?.bottom;
    if (isPercentDimension(value)) {
      const numberValue = getNumber(value);
      return (Number(numberValue) / 100) * (parent.height - height);
    }
    return value;
  };

  const getRightValue = () => {
    const value = obj.style?.right;
    if (isPercentDimension(value)) {
      const numberValue = getNumber(value);
      return (Number(numberValue) / 100) * (parent.width - width);
    }
    return value;
  };

  return {
    ...(obj.style?.top !== undefined ? { top: getTopValue() } : {}),
    ...(obj?.style?.left !== undefined ? { left: getLeftValue() } : {}),
    ...(obj?.style?.bottom !== undefined ? { bottom: getBottomValue() } : {}),
    ...(obj?.style?.right !== undefined ? { right: getRightValue() } : {}),
  };
};

/**
 * There're 3 type of object render:
 * 1. Fixed position
 * 2. Absolute position
 * 3. Relative position: Only used when parent is an autolayout
 */
const ObjectRenderV4 = ({
  arrComp,
  isScreen,
  layout,
  isShowStatusBar,
  screen,
  isAutoLayout,
}: ObjectRenderProps) => {
  const dimension = useSelector(dimensionSelector);
  const appInfo = useSelector(appInfoSelector);
  const insets = useSafeAreaInsets();
  const isV4 = appInfo?.version == AppVersion.V4;

  const { search } = useLocation();

  const query: ParsedQuery<any> = qs.parse(search);

  const pages = useSelector(pagesSelector);

  const currentPageInfo = useMemo(
    () => (screen ? screen : pages[query?.target]),
    [pages, query, screen]
  );

  const originX = currentPageInfo ? dimension.width / currentPageInfo.width : 0;
  const originY = currentPageInfo
    ? dimension.height / currentPageInfo.height
    : 0;

  const isWeb = appInfo?.platform === Platform.WEB;
  const isResponsive = appInfo?.platform === Platform.RESPONSIVE;
  const statusBarHeight = isWeb ? 0 : HEIGHT_STATUS_BAR * originX;

  // On iOS, StatusBar.currentHeight is undefined, so use safe area insets
  // On Android, use StatusBar.currentHeight
  const currentStatusBarHeight =
    PlatformRN.OS === 'ios'
      ? insets.top
      : PlatformRN.OS === 'android' && StatusBar.currentHeight
      ? StatusBar.currentHeight
      : 0;

  const offsetLeft = PlatformRN.OS === 'web' ? layout?.offsetLeft || 0 : 0;
  const offsetTop = layout?.offsetTop || 0;
  // do not sort if parent is auto layout
  const sortArrComp: ElementType[] = isAutoLayout
    ? arrComp
    : sortBy(arrComp, ['y', 'x']);

  if (!sortArrComp.length) return <Fragment />;

  let res = [];

  for (let i = 0; i < sortArrComp.length; i++) {
    let obj = JSON.parse(JSON.stringify(sortArrComp[i])),
      prevObj = sortArrComp[i - 1];
    let ObjectClass = mappingV4[obj.type] as FC;

    if (!ObjectClass) {
      console.log(`I don't know how to render ${obj.name}`);
      res.push(<React.Fragment key={i}></React.Fragment>);
      continue;
    }

    // add screenUuid to obj
    if (screen?.screenUuid) {
      obj.screenUuid = screen?.screenUuid;
    }

    // update colors
    if (appInfo) {
      obj = updateElementColor(obj, appInfo);
    }

    // mapping v3 style to v4 style
    obj = isV4 ? obj : mapV3StyleToV4Style(obj);

    // scale object width to screen width on v4-mobile
    // if (appInfo?.platform === Platform.MOBILE) {
    //   if (obj.width) {
    //     obj.width = (obj.width / currentPageInfo.width) * dimension.width;
    //   }
    //   if (obj.style?.width) {
    //     obj.style.width =
    //       (obj.style.width / currentPageInfo.width) * dimension.width;
    //   }
    // }

    const position = obj.style?.position || 'absolute'; // default position is absolute
    if (position === 'absolute') {
      const positionStyle = calculateObjectPositionStyle(obj, {
        width: dimension.width - offsetLeft,
        height: dimension.height - offsetTop - (i === 0 ? statusBarHeight : 0),
      });
      const safeTopElementiOS = ['ActionButton'];
      // Add StatusBar height to top position if it exists and is at screen level
      // Also add left/right insets for iOS to handle notch
      const adjustedPositionStyle =
        PlatformRN.OS === 'android'
          ? {
              ...positionStyle,
              ...(positionStyle.top !== undefined && isScreen
                ? {
                    top:
                      typeof positionStyle.top === 'number'
                        ? positionStyle.top + currentStatusBarHeight
                        : positionStyle.top,
                  }
                : {}),
              ...(positionStyle.left !== undefined && isScreen
                ? {
                    left:
                      typeof positionStyle.left === 'number'
                        ? positionStyle.left + insets.left
                        : positionStyle.left,
                  }
                : {}),
              ...(positionStyle.right !== undefined && isScreen
                ? {
                    right:
                      typeof positionStyle.right === 'number'
                        ? positionStyle.right + insets.right
                        : positionStyle.right,
                  }
                : {}),
            }
          : {
              ...positionStyle,
              ...(PlatformRN.OS === 'ios' &&
              safeTopElementiOS.includes(obj.componentName)
                ? {
                    top:
                      typeof positionStyle.top === 'number'
                        ? positionStyle.top + currentStatusBarHeight
                        : positionStyle.top,
                  }
                : {}),
            };

      obj = {
        ...obj,
        style: {
          ...obj.style,
          width: obj.style?.width,
          height: obj.style?.height,
          ...adjustedPositionStyle,
          ...(adjustedPositionStyle.left === '50%'
            ? {
                transform: [{ translateX: '-50%' }],
              }
            : {}),
          ...(adjustedPositionStyle.left === '50%' &&
          adjustedPositionStyle.top === '50%'
            ? {
                transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
              }
            : {}),
          position: 'absolute',
          zIndex: obj.zIndex || i,
        },
      };

      res.push(
        <BindingComponent
          nativeID={`object-render-absolute-${obj.componentName}-${obj.id}`}
          key={i}
          obj={obj}
          ObjectClass={ObjectClass}
        />
      );
      continue;
    }

    if (position === 'relative') {
      // Copy margin values from style to top-level properties
      // Components like Rectangle expect margins at top level (Rectangle/style.ts line 49)
      const marginTop = obj.style?.marginTop;
      const marginLeft = obj.style?.marginLeft;
      const marginRight = obj.style?.marginRight;
      const marginBottom = obj.style?.marginBottom;

      // Add StatusBar height to marginTop if it's the first element at screen level
      const adjustedMarginTop =
        marginTop !== undefined && i === 0 && isScreen
          ? typeof marginTop === 'number'
            ? marginTop + currentStatusBarHeight
            : marginTop
          : marginTop;

      obj = {
        ...obj,
        style: omit(obj.style, ['top', 'left', 'right', 'bottom']),
        // Apply margins to top-level if they exist in style
        ...(adjustedMarginTop !== undefined && {
          marginTop: adjustedMarginTop,
        }),
        ...(marginLeft !== undefined && { marginLeft }),
        ...(marginRight !== undefined && { marginRight }),
        ...(marginBottom !== undefined && { marginBottom }),
      };

      res.push(
        <BindingComponent
          nativeID={`object-render-relative-${obj.componentName}-${obj.id}`}
          key={i}
          obj={obj}
          ObjectClass={ObjectClass}
        />
      );
      continue;
    }

    if (position === 'fixed') {
      const positionStyle = calculateObjectPositionStyle(obj, {
        width: dimension.width - offsetLeft,
        height: dimension.height - offsetTop,
      });
      const safeElement = ['Header'];
      obj = {
        ...obj,
        style: {
          ...obj.style,
          width: obj.style?.width || obj.width,
          height: obj.style?.height || obj.height,
          ...positionStyle,
          ...(positionStyle.left === '50%'
            ? {
                transform: [{ translateX: '-50%' }],
              }
            : {}),
          ...(positionStyle.left === '50%' && positionStyle.top === '50%'
            ? {
                transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
              }
            : {}),
          position:
            isResponsive && PlatformRN.OS === 'web'
              ? position
              : safeElement.includes(obj.componentName)
              ? position
              : 'absolute',
          zIndex: obj.style?.zIndex || i,
        },
      };

      res.push(
        <BindingComponent
          nativeID={`object-render-fixed-${obj.componentName}-${obj.id}`}
          key={i}
          obj={obj}
          ObjectClass={ObjectClass}
        />
      );
      continue;
    }
  }

  return <React.Fragment>{res}</React.Fragment>;
};

export default ObjectRenderV4;
